import React, { useEffect, useRef, useState } from 'react';
import { Input, message, Modal, Select } from 'antd';
import classNames from 'classnames';
import CommonStore from 'store/Common';
import { ServerMeta, TAllergy } from 'types/common';
import s from './s.module.less';
import { getMedicationsByCondition, updatePatientCondition } from 'api/textVisit';
import { gpUpdatePatientCondition } from 'api/group';
import { getKlarityServiceSpecialtyCondition, getListSettingByType } from 'api/common';
import { getOpsKlarityServiceSpecialtyCondition } from 'api/operation';
import { EnumFields } from 'types/enumerationData';
import AllergiesSelect from './AllergiesSelect';
import { isOpPortal } from 'api';

export enum PatientUpdateType {
    MedicalHistory = 'MedicalHistory',
    CurrentMedications = 'CurrentMedications',
    Allergies = 'Allergies',
}

type Props = {
    open: boolean;
    type: PatientUpdateType;
    onCancel: () => void;
    onUpdated: () => void;
    prospectId?: number;
    conditionHealth?: {
        featured: string[];
        notSure: boolean;
    };
    medicationHealth?: {
        medications: string[];
        notTaking: boolean;
        others: string[];
    };
    allergyHealth?: {
        hasAllergy: boolean;
        allergies: TAllergy[];
    };
    isGroupPractice?: boolean;
};

const titleMap = {
    [PatientUpdateType.MedicalHistory]: 'Edit medical History',
    [PatientUpdateType.CurrentMedications]: 'Edit current Medications',
    [PatientUpdateType.Allergies]: 'Edit allergies',
};

const NOT_SURE_OPTION = 'I’m not sure';
const NOT_TAKING_MEDICATIONS_OPTION = 'I’m not taking any medications';
const OTHER_MEDICATIONS_OPTION = 'Others';

const TELEHEALTH_VISIT_PRESCRIPTION_REFILL_DICTKEY = 'telehealth-visit-prescription-refill';

const initialPendingValues = {
    specialty: '',
    conditions: [],
    medications: [],
};

export default function PatientUpdateDialog(props: Props) {
    const { open, onCancel, type } = props;
    const [getCommon] = CommonStore.useStore();
    const featureConditionFromStore = getCommon('featuredCondition');
    const [featureOption, setFeatureOption] = useState<ServerMeta[]>([]);
    const [conditions, setConditions] = useState<ServerMeta[]>([]);
    const [medications, setMedications] = useState<string[]>([]);
    const [currentSelectedSpecialty, setCurrentSelectedSpecialty] = useState('');
    const [klarityServiceSpecialtyCondition, setKlarityServiceSpecialtyCondition] = useState<
        { name: string; conditions: string[] }[]
    >([]);
    const [showMoreConditions, setShowMoreConditions] = useState(false);
    const [notSureCondition, setNotSureCondition] = useState(false);
    const [notTakingMedicationsChecked, setNotTakingMedicationsChecked] = useState(false);
    const [otherMedicationChecked, setOtherMedicationChecked] = useState(false);
    const [otherMedicationValue, setOtherMedicationValue] = useState('');
    const specialitySelectedConditions = useRef<Record<string, string[]>>({});
    const [pendingValues, setPendingValues] = useState<{
        specialty: string;
        conditions: string[];
        medications: string[];
    }>({ ...initialPendingValues });
    const [allergies, setAllergies] = useState<TAllergy[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const targetSpecialityConditions = React.useMemo(() => {
        const conditonStr: string[] =
            klarityServiceSpecialtyCondition.find((item) => item.name === currentSelectedSpecialty)?.conditions || [];
        return conditions?.filter((item) => {
            if (conditonStr) {
                return conditonStr.includes(item.dictKey) || conditonStr.includes(item.content);
            }
            return false;
        });
    }, [klarityServiceSpecialtyCondition, currentSelectedSpecialty, conditions]);

    useEffect(() => {
        const isOps = isOpPortal();
        let fn = getKlarityServiceSpecialtyCondition;
        if (isOps) {
            fn = getOpsKlarityServiceSpecialtyCondition;
        }
        fn().then((res) => {
            const data = res.data || {};
            const newSpecialties: { name: string; conditions: string[] }[] = Object.keys(data).map((spe) => ({
                name: spe,
                conditions: data[spe] || [],
            }));
            setKlarityServiceSpecialtyCondition(newSpecialties);
        });
    }, []);

    useEffect(() => {
        getListSettingByType(EnumFields.HELLO_KLARITY_CONDITION).then((res) => {
            const record = res?.data?.data || [];
            const caData: ServerMeta[] =
                record.sort((a: ServerMeta, b: ServerMeta) => {
                    const stringA = a.content.toLowerCase();
                    const stringB = b.content.toLowerCase();
                    if (stringA < stringB) {
                        return -1;
                    }
                    if (stringA > stringB) {
                        return 1;
                    }
                    return 0;
                }) || [];

            const data = caData?.filter((item) => item.dictKey !== TELEHEALTH_VISIT_PRESCRIPTION_REFILL_DICTKEY);
            setConditions(data);
        });
    }, []);

    const hanldeNotSureOption = () => {
        const newValue = !notSureCondition;
        setPendingValues({
            ...pendingValues,
            conditions: [],
        });
        setShowMoreConditions(false);
        setCurrentSelectedSpecialty('');
        specialitySelectedConditions.current = {};
        setNotSureCondition(newValue);
    };

    const handleFeatureConditionSelect = (condition: string) => {
        const current = pendingValues?.conditions || [];
        if (current.includes(condition)) {
            current.splice(current.indexOf(condition), 1);
        } else {
            current.push(condition);
        }
        setPendingValues({
            ...pendingValues,
            conditions: current,
        });
        setNotSureCondition(false);
    };

    const handleTargetSpecialityConditionSelect = (condition: string) => {
        const current = pendingValues.conditions || [];
        if (current.includes(condition)) {
            current.splice(current.indexOf(condition), 1);
            const currentSpecialityConditions = specialitySelectedConditions.current[currentSelectedSpecialty!] || [];
            currentSpecialityConditions.splice(currentSpecialityConditions.indexOf(condition), 1);
            specialitySelectedConditions.current[currentSelectedSpecialty!] = currentSpecialityConditions;
        } else {
            current.push(condition);
            const currentSpecialityConditions = specialitySelectedConditions.current[currentSelectedSpecialty!] || [];
            currentSpecialityConditions.push(condition);
            specialitySelectedConditions.current[currentSelectedSpecialty!] = currentSpecialityConditions;
        }
        setPendingValues({ ...pendingValues, conditions: current });
        setNotSureCondition(false);
    };

    async function handleMedicationSelect(medication: string) {
        const current = pendingValues?.medications || [];
        if (current.includes(medication)) {
            current.splice(current.indexOf(medication), 1);
        } else {
            current.push(medication);
        }
        setPendingValues({
            ...pendingValues,
            medications: current,
        });
        setNotTakingMedicationsChecked(false);
    }

    const handleAllergiesChange = (allergies: TAllergy[]) => {
        setAllergies(allergies);
    };

    async function handleSaveCondition() {
        if (props.prospectId) {
            setIsSubmitting(true);
            const conditon = pendingValues.conditions;
            const specialtys: { specialtyName: string; conditionName: string }[] = [];
            conditon.forEach((condition) => {
                const specialty = klarityServiceSpecialtyCondition.find((item) => item.conditions.includes(condition));
                if (specialty) {
                    specialtys.push({
                        specialtyName: specialty.name,
                        conditionName: condition,
                    });
                }
            });

            const featured = featureOption
                .filter((item) => pendingValues.conditions.includes(item.content))
                .map((item) => item.content);

            try {
                const saveApi = props.isGroupPractice ? gpUpdatePatientCondition : updatePatientCondition;
                await saveApi(props.prospectId, {
                    condition: {
                        featured,
                        notSure: notSureCondition,
                        specialty: specialtys,
                    },
                });
                props.onUpdated();
                props.onCancel();
                message.success(`${titleMap[type]} updated successfully`);
            } catch (error) {
                console.error(error);
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    async function handleSaveMedications() {
        if (props.prospectId) {
            setIsSubmitting(true);
            const { medications } = pendingValues;

            try {
                const saveMedApi = props.isGroupPractice ? gpUpdatePatientCondition : updatePatientCondition;
                await saveMedApi(props.prospectId, {
                    medications: {
                        medications,
                        notTaking: notTakingMedicationsChecked,
                        others: otherMedicationValue?.split(',') || [],
                    },
                });
                props.onUpdated();
                props.onCancel();
                message.success(`${titleMap[type]} updated successfully`);
            } catch (error) {
                console.error(error);
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    async function handleSaveAllergies() {
        if (props.prospectId) {
            setIsSubmitting(true);

            try {
                const saveAllergyApi = props.isGroupPractice ? gpUpdatePatientCondition : updatePatientCondition;
                await saveAllergyApi(props.prospectId, {
                    allergies: {
                        hasAllergy: allergies.length > 0,
                        allergies: allergies.map((item) => ({
                            allergyType: item.allergyType,
                            allergyId: item.allergyId,
                            routedMedId: item.routedMedId,
                            descr: item.descr,
                        })),
                    },
                });
                props.onUpdated();
                props.onCancel();
                message.success(`${titleMap[type]} updated successfully`);
            } catch (error) {
                console.error(error);
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    function handleSave() {
        if (type === PatientUpdateType.MedicalHistory) {
            handleSaveCondition();
        }
        if (type === PatientUpdateType.CurrentMedications) {
            handleSaveMedications();
        }
        if (type === PatientUpdateType.Allergies) {
            handleSaveAllergies();
        }
    }

    const renderConditionSelect = () => {
        return (
            <div>
                <div className={s.list}>
                    {featureOption.map((item, index) => (
                        <div
                            key={index}
                            className={classNames(s.item, {
                                [s.active]: pendingValues.conditions.includes(item.content),
                            })}
                            onClick={() => handleFeatureConditionSelect(item.content)}
                        >
                            {item.content}
                        </div>
                    ))}

                    <div className={classNames(s.item, { [s.active]: notSureCondition })} onClick={hanldeNotSureOption}>
                        {NOT_SURE_OPTION}
                    </div>
                </div>
                <div className={s.viewMoreRow} onClick={() => setShowMoreConditions(!showMoreConditions)}>
                    {showMoreConditions ? 'Show less' : 'View more conditions'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                            d={
                                showMoreConditions
                                    ? 'M4.16732 12.5L10.0007 6.66667L15.834 12.5'
                                    : 'M15.8327 7.5L9.99935 13.3333L4.16602 7.5'
                            }
                            stroke="#6B7280"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
                {showMoreConditions && (
                    <div className={s.specialtyConditionWrap}>
                        <div className={s.list}>
                            {klarityServiceSpecialtyCondition.map((item) => {
                                const isSelect = currentSelectedSpecialty === item.name;
                                const allCondtion = pendingValues?.conditions || [];
                                const condtionInSpeciality =
                                    klarityServiceSpecialtyCondition.find((val) => val.name === item.name)
                                        ?.conditions || [];
                                const amount = allCondtion.filter((cond) => condtionInSpeciality.includes(cond)).length;
                                return (
                                    <div
                                        key={item.name}
                                        className={classNames(s.speItem, { [s.selected]: isSelect })}
                                        onClick={() => setCurrentSelectedSpecialty(item.name)}
                                    >
                                        {item.name}
                                        {amount > 0 && ` (${amount})`}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={s.list}>
                            {targetSpecialityConditions.map((item) => {
                                const isSelect = pendingValues.conditions.includes(item.content);
                                return (
                                    <div
                                        key={item.content}
                                        className={classNames(s.item, { [s.active]: isSelect })}
                                        onClick={() => handleTargetSpecialityConditionSelect(item.content)}
                                    >
                                        {item.content}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderMedications = () => {
        return (
            <div>
                <div className={s.list}>
                    {medications.map((medication, index) => (
                        <div
                            key={index}
                            className={classNames(s.item, {
                                [s.active]: pendingValues.medications.includes(medication),
                            })}
                            onClick={() => handleMedicationSelect(medication)}
                        >
                            {medication}
                        </div>
                    ))}
                    <div
                        className={classNames(s.item, { [s.active]: otherMedicationChecked })}
                        onClick={() => {
                            setOtherMedicationChecked(true);
                            setNotTakingMedicationsChecked(false);
                        }}
                    >
                        {OTHER_MEDICATIONS_OPTION}
                    </div>
                    <div
                        className={classNames(s.item, { [s.active]: notTakingMedicationsChecked })}
                        onClick={() => {
                            setPendingValues({ ...pendingValues, medications: [] });
                            setNotTakingMedicationsChecked(true);
                            setOtherMedicationChecked(false);
                            setOtherMedicationValue('');
                        }}
                    >
                        {NOT_TAKING_MEDICATIONS_OPTION}
                    </div>
                    {otherMedicationChecked && (
                        <div className={s.otherMedication}>
                            <Input
                                placeholder="List any other medications you're taking..."
                                value={otherMedicationValue}
                                size="large"
                                className={s.otherMedicationInput}
                                onChange={(e) => {
                                    setNotTakingMedicationsChecked(false);
                                    setOtherMedicationValue(e.target.value);
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderAllergies = () => {
        return (
            <div>
                <p className={s.allergiesTitle}>Allergies</p>
                <AllergiesSelect value={allergies} onChange={handleAllergiesChange} />
            </div>
        );
    };

    const renderContent = () => {
        if (type === PatientUpdateType.MedicalHistory) {
            return renderConditionSelect();
        }
        if (type === PatientUpdateType.CurrentMedications) {
            return renderMedications();
        }
        return renderAllergies();
    };

    async function initEditMedications() {
        const featured = props.conditionHealth?.featured || [];
        const currentMedications = props.medicationHealth?.medications || [];
        const others = props.medicationHealth?.others || [];
        const res = await getMedicationsByCondition(featured);
        if (!res.error) {
            const data: {
                id: number;
                conditionName: string;
                medication: string;
                sort: number;
            }[] = res?.data;

            if (data) {
                const _medications = new Set<string>();
                data.forEach((item) => item.medication && _medications.add(item.medication));
                setMedications(Array.from(_medications));
            }
        }

        setPendingValues({ ...initialPendingValues, medications: [...currentMedications] });

        setOtherMedicationValue(others.join(','));
        setOtherMedicationChecked(others.length > 0);

        setNotTakingMedicationsChecked(props.medicationHealth?.notTaking || false);
    }

    function initEditHistory() {
        const featured = props.conditionHealth?.featured || [];
        setPendingValues({ ...initialPendingValues, conditions: [...featured] });
        setNotSureCondition(props.conditionHealth?.notSure || false);
    }

    function initEditAllergies() {
        const { allergyHealth } = props;
        setAllergies(allergyHealth?.allergies || []);
    }

    useEffect(() => {
        if (open && type === PatientUpdateType.MedicalHistory) {
            initEditHistory();
            return;
        }
        if (open && type === PatientUpdateType.CurrentMedications) {
            initEditMedications();
            return;
        }

        if (open && type === PatientUpdateType.Allergies) {
            initEditAllergies();
        }
    }, [open, type]);

    useEffect(() => {
        if (featureConditionFromStore) {
            setFeatureOption(featureConditionFromStore);
        }
    }, [featureConditionFromStore]);

    return (
        <Modal
            title={titleMap[type]}
            destroyOnClose
            open={open}
            onCancel={props.onCancel}
            width={900}
            okText="Save"
            onOk={handleSave}
            confirmLoading={isSubmitting}
            zIndex={1050}
        >
            <div className={s.list}>{renderContent()}</div>
        </Modal>
    );
}
